import React, { useState } from 'react';
import { cn } from '../../utils/cn';
import {
  Calendar,
  ChevronDown,
  Link,
  Send,
  User,
  Check,
  Mail,
  ChevronUp,
  MousePointer,
  HelpCircle,
  Plus,
  BarChart3,
  Download,
  MoreHorizontal,
  RefreshCw,
} from 'lucide-react';

interface MetricsDashboardProps {
  className?: string;
}

const MetricsDashboard: React.FC<MetricsDashboardProps> = ({ className }) => {
  const [dateRange, setDateRange] = useState('Jul 28, 2025 - Aug 26, 2025');
  const [frequency, setFrequency] = useState('Daily');
  const [metricType, setMetricType] = useState('percentage'); // 'count' or 'percentage'
  const [metricCategory, setMetricCategory] = useState('delivery'); // 'delivery' or 'journey'

  // Define metric type
  type Metric = {
    name: string;
    checked: boolean;
    percentage: string;
    count: string;
    total: string;
    color: string;
    link?: string;
  };

  // Delivery Metrics Data
  const deliveryMetrics: Metric[] = [
    {
      name: 'Sent',
      checked: true,
      percentage: '100.0%',
      count: '3',
      total: '3 created',
      color: 'blue',
    },
    {
      name: 'Delivered',
      checked: false,
      percentage: '66.7%',
      count: '2',
      total: '3 sent',
      color: 'gray',
    },
    {
      name: 'Opened',
      checked: true,
      percentage: '50.0%',
      count: '1',
      total: '2 delivered',
      color: 'emerald',
      link: 'Learn about human opens',
    },
    {
      name: 'Clicked',
      checked: true,
      percentage: '0.0%',
      count: '0',
      total: '2 delivered',
      color: 'emerald',
      link: 'Learn about human clicks',
    },
    {
      name: 'Click-to-open',
      checked: false,
      percentage: '0.0%',
      count: '0',
      total: '1 opened',
      color: 'gray',
    },
    {
      name: 'Converted',
      checked: false,
      percentage: '0.0%',
      count: '0',
      total: '2 delivered',
      color: 'gray',
    },
    {
      name: 'Unsubscribed',
      checked: false,
      percentage: '0.0%',
      count: '0',
      total: '2 delivered',
      color: 'red',
    },
  ];

  // Journey Metrics Data
  const journeyMetrics: Metric[] = [
    {
      name: 'Triggered',
      checked: false,
      percentage: '100.0%',
      count: '3',
      total: '3 triggered',
      color: 'gray',
    },
    {
      name: 'Started',
      checked: true,
      percentage: '100.0%',
      count: '3',
      total: '3 triggered',
      color: 'blue',
    },
    {
      name: 'Messaged',
      checked: true,
      percentage: '100.0%',
      count: '3',
      total: '3 triggered',
      color: 'blue',
    },
    {
      name: 'In progress',
      checked: true,
      percentage: '0.0%',
      count: '0',
      total: '3 triggered',
      color: 'blue',
    },
    {
      name: 'Completed',
      checked: true,
      percentage: '100.0%',
      count: '3',
      total: '3 triggered',
      color: 'emerald',
    },
    {
      name: 'Filtered out',
      checked: true,
      percentage: '0.0%',
      count: '0',
      total: '3 triggered',
      color: 'orange',
    },
    {
      name: 'Exited early',
      checked: true,
      percentage: '0.0%',
      count: '0',
      total: '3 triggered',
      color: 'amber',
    },
    {
      name: 'Converted',
      checked: true,
      percentage: '0.0%',
      count: '0',
      total: '3 triggered',
      color: 'emerald',
    },
  ];

  const messageMetrics = [
    {
      actionName: 'Email 1',
      sent: '3',
      delivered: { percentage: '66.7%', count: '2' },
      opened: { percentage: '50.0%', count: '1' },
      clicked: { percentage: '0.0%', count: '0' },
      unsubscribed: { percentage: '0.0%', count: '0' },
    },
  ];

  const getMetricColor = (color: string) => {
    switch (color) {
      case 'blue':
        return 'text-primary';
      case 'emerald':
        return 'text-success';
      case 'red':
        return 'text-destructive';
      case 'orange':
      case 'amber':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getCheckIcon = (checked: boolean, color: string) => {
    if (checked) {
      return <Check className="w-4 h-4 text-primary" />;
    }
    if (color === 'red') {
      return <div className="w-4 h-4 border border-destructive rounded"></div>;
    }
    return <div className="w-4 h-4 border border-border rounded"></div>;
  };

  return (
    <div
      className={cn(
        'w-full h-full bg-gradient-to-br from-muted to-background',
        className
      )}
    >
      {/* Enhanced Sub-navigation and Filters */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Enhanced Date Range Picker */}
              <div className="flex items-center space-x-3">
                <Calendar className="w-4 h-4 text-gray-500" />
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                >
                  <option>Jul 28, 2025 - Aug 26, 2025</option>
                </select>
              </div>

              {/* Enhanced Frequency Dropdown */}
              <div className="flex items-center space-x-3">
                <BarChart3 className="w-4 h-4 text-gray-500" />
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                >
                  <option>Daily</option>
                  <option>Weekly</option>
                  <option>Monthly</option>
                </select>
              </div>

              {/* Enhanced Toggle Buttons */}
              <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setMetricType('count')}
                  className={cn(
                    'px-3 py-2 text-sm rounded-md transition-all duration-200',
                    metricType === 'count'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  Count
                </button>
                <button
                  onClick={() => setMetricType('percentage')}
                  className={cn(
                    'px-3 py-2 text-sm rounded-md transition-all duration-200',
                    metricType === 'percentage'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  Percentage
                </button>
              </div>

              {/* Enhanced Metric Category Tabs */}
              <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setMetricCategory('delivery')}
                  className={cn(
                    'flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200',
                    metricCategory === 'delivery'
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <Send className="w-4 h-4" />
                  <span>Delivery</span>
                </button>
                <button
                  onClick={() => setMetricCategory('journey')}
                  className={cn(
                    'flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200',
                    metricCategory === 'journey'
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <User className="w-4 h-4" />
                  <span>Journey</span>
                </button>
              </div>
            </div>

            {/* Enhanced Action Buttons */}
            <div className="flex items-center space-x-3">
              <button className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted/80 transition-colors">
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
              <button className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-hover transition-colors">
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Main Content */}
      <div className="flex-1 p-6 space-y-6">
        {/* Conditional Cards Based on Metric Category */}
        {metricCategory === 'delivery' ? (
          /* Enhanced Delivery Metrics Card */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Enhanced Chart Area */}
              <div className="lg:col-span-3">
                {/* Enhanced Chart Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Performance & Delivery Metrics
                    </h3>
                    <Link className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                  </div>
                  <div className="flex items-center space-x-3">
                    <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary">
                      <option>Aggregate</option>
                    </select>
                    <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary">
                      <option>Email</option>
                    </select>
                    <Mail className="w-4 h-4 text-gray-400" />
                  </div>
                </div>

                {/* Enhanced Chart Placeholder */}
                <div className="h-80 bg-gradient-to-br from-muted to-background rounded-xl flex items-center justify-center border border-border">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-primary-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <BarChart3 className="w-10 h-10 text-primary" />
                    </div>
                    <p className="text-sm text-gray-600 font-medium">
                      Line chart showing delivery performance metrics
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      Blue line: 100% peak, Green line: 50% peak
                    </p>
                  </div>
                </div>
              </div>

              {/* Enhanced Right Side Panel - Detailed Metrics */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4">
                  Detailed Metrics
                </h4>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {deliveryMetrics.map((metric, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getCheckIcon(metric.checked, metric.color)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            {metric.name}
                          </span>
                          <span
                            className={cn(
                              'text-sm font-semibold',
                              getMetricColor(metric.color)
                            )}
                          >
                            {metric.percentage}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-gray-500">
                            {metric.count} out of {metric.total}
                          </span>
                          {metric.link && (
                            <button className="text-xs text-primary hover:text-primary-hover transition-colors">
                              {metric.link}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Enhanced Journey Metrics Cards */
          <div className="space-y-6">
            {/* Enhanced Journey Metrics Overview Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Journey Metrics
                  </h3>
                  <Link className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                  <button className="text-primary hover:text-primary-hover text-sm font-medium transition-colors flex items-center">
                    What can journeys tell me?
                    <HelpCircle className="w-4 h-4 ml-1" />
                  </button>
                </div>
                <button className="text-primary hover:text-primary-hover text-sm font-medium transition-colors flex items-center">
                  Definitions
                  <HelpCircle className="w-4 h-4 ml-1" />
                </button>
              </div>

              {/* Enhanced Journey Chart */}
              <div className="h-80 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl flex items-center justify-center border border-gray-200 mb-6">
                <div className="text-center">
                  <div className="w-20 h-20 bg-success-background rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-10 h-10 text-success" />
                  </div>
                  <p className="text-sm text-gray-600 font-medium">
                    Line chart showing journey progression metrics
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Green line: 0% baseline, Blue/Green spikes: 100% events
                  </p>
                </div>
              </div>

              {/* Enhanced Journey Summary */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4">
                  Journey Summary
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {journeyMetrics.map((metric, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getCheckIcon(metric.checked, metric.color)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            {metric.name}
                          </span>
                          <span
                            className={cn(
                              'text-sm font-semibold',
                              getMetricColor(metric.color)
                            )}
                          >
                            {metric.percentage}
                          </span>
                        </div>
                        <div className="mt-1">
                          <span className="text-xs text-gray-500">
                            {metric.count} out of {metric.total}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Links Section - Only show for Delivery Metrics */}
        {metricCategory === 'delivery' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Link className="w-4 h-4 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Links</h3>
                <p className="text-sm text-gray-500">
                  Tracked link performance
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  July 27, 2025 to August 25, 2025
                </span>
                <div className="flex items-center space-x-2 px-3 py-1 bg-primary-background rounded-full">
                  <span className="text-sm font-medium text-primary">
                    TOTAL CLICKS
                  </span>
                  <HelpCircle className="w-4 h-4 text-primary" />
                </div>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700">
                TOP CLICKED LINKS (ENTIRE CAMPAIGN)
              </p>
            </div>

            {/* Enhanced Empty State */}
            <div className="text-center py-12">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <MousePointer className="w-8 h-8 text-gray-400" />
                </div>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                No tracked link clicks found
              </h4>
              <p className="text-sm text-gray-500 mb-4">
                Start tracking links to see performance data
              </p>
              <button className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-primary bg-primary-background rounded-lg hover:bg-primary-muted transition-colors">
                <Link className="w-4 h-4" />
                <span>Learn more about tracking links</span>
              </button>
            </div>
          </div>
        )}

        {/* Enhanced Message Metrics Section - Only show for Delivery Metrics */}
        {metricCategory === 'delivery' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Link className="w-4 h-4 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Message Metrics
                </h3>
                <p className="text-sm text-gray-500">Performance by message</p>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  July 27, 2025 to August 25, 2025
                </span>
                <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary">
                  <option>All channels</option>
                </select>
              </div>
            </div>

            {/* Enhanced Message Metrics Table */}
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">
                      <div className="flex items-center">
                        ACTION NAME
                        <ChevronUp className="w-4 h-4 ml-1" />
                      </div>
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">
                      <div className="flex items-center">
                        SENT
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </div>
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">
                      <div className="flex items-center">
                        DELIVERED
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </div>
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">
                      <div className="flex items-center">
                        OPENED
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </div>
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">
                      <div className="flex items-center">
                        CLICKED
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </div>
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">
                      <div className="flex items-center">
                        UNSUBSCRIBED
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </div>
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">
                      <Plus className="w-4 h-4" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {messageMetrics.map((metric, index) => (
                    <tr
                      key={index}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-900">
                            {metric.actionName}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-sm font-medium text-gray-900">
                          {metric.sent}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {metric.delivered.percentage}
                          </span>
                          <span className="text-xs text-gray-500">
                            {metric.delivered.count}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {metric.opened.percentage}
                          </span>
                          <span className="text-xs text-gray-500">
                            {metric.opened.count}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {metric.clicked.percentage}
                          </span>
                          <span className="text-xs text-gray-500">
                            {metric.clicked.count}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {metric.unsubscribed.percentage}
                          </span>
                          <span className="text-xs text-gray-500">
                            {metric.unsubscribed.count}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <button className="text-gray-400 hover:text-gray-600 transition-colors">
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricsDashboard;
